/**
 * FormFlu API Client
 * 
 * HIPAA COMPLIANCE NOTES:
 * - All API calls go through this client
 * - NO PHI logging in console or error messages
 * - Token stored in memory (not localStorage for PHI)
 * - Automatic token refresh handling
 */

import type {
  ApiResponse,
  ApiError,
  LoginRequest,
  LoginResponse,
  RegistrationRequest,
  RegistrationResponse,
  MFAVerifyRequest,
  MFASetupData,
  MFASetupVerifyRequest,
  TokenResponse,
  TokenWithRecoveryResponse,
  MFARecoveryRequest,
  User,
  Tenant,
  TenantListItem,
  TenantSwitchResponse,
  FormListItem,
  Form,
  FormCreateRequest,
  FormUpdateRequest,
  StepCreateRequest,
  VariantCreateRequest,
  FieldCreateRequest,
  ReorderRequest,
  PublishRequest,
  PublishResponse,
  EmbedCodeResponse,
  FormStep,
  StepVariant,
  FormField,
  SubmissionDetail,
  DecryptedSubmissionData,
  DecryptRequest,
  SubmissionExportRequest,
  BulkActionRequest,
  BulkActionResponse,
  SubmissionStats,
  PaginatedSubmissions,
  AnalyticsOverview,
  FunnelData,
  ABTestResults,
  TeamMember,
  InviteUserRequest,
  UpdateUserRoleRequest,
} from '@formflow/shared';

// =============================================================================
// Configuration
// =============================================================================

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

// Token storage (in memory for security - not localStorage)
let accessToken: string | null = null;
let refreshToken: string | null = null;
let mfaToken: string | null = null;

// Token refresh promise to prevent multiple simultaneous refreshes
let refreshPromise: Promise<string> | null = null;

// =============================================================================
// Token Management
// =============================================================================

export function setTokens(access: string, refresh: string): void {
  accessToken = access;
  refreshToken = refresh;
  // Also persist refresh token to survive page refresh
  // Using sessionStorage for security (clears on browser close)
  try {
    sessionStorage.setItem('ff_refresh', refresh);
  } catch {
    // Session storage might be unavailable
  }
}

export function setMfaToken(token: string): void {
  mfaToken = token;
}

export function clearTokens(): void {
  accessToken = null;
  refreshToken = null;
  mfaToken = null;
  try {
    sessionStorage.removeItem('ff_refresh');
  } catch {
    // Session storage might be unavailable
  }
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function getMfaToken(): string | null {
  return mfaToken;
}

// Initialize from session storage on load
export function initializeAuth(): void {
  try {
    const storedRefresh = sessionStorage.getItem('ff_refresh');
    if (storedRefresh) {
      refreshToken = storedRefresh;
    }
  } catch {
    // Session storage might be unavailable
  }
}

// =============================================================================
// API Error Class
// =============================================================================

/** Field-level validation errors from Django */
export type FieldErrors = Record<string, string[]>;

/**
 * Parse Django validation errors into a structured format
 * Django can return errors like:
 * - { "password": ["error1", "error2"] }
 * - { "non_field_errors": ["error"] }
 * - { "detail": "error" } or { "message": "error" }
 */
function parseApiError(_status: number, data: unknown): { error: ApiError; fieldErrors: FieldErrors } {
  const fieldErrors: FieldErrors = {};
  
  // Handle null/undefined response
  if (!data) {
    return {
      error: { code: 'UNKNOWN_ERROR', message: 'An unexpected error occurred' },
      fieldErrors,
    };
  }
  
  // If it's already in our expected format
  if (typeof data === 'object' && data !== null) {
    const obj = data as Record<string, unknown>;
    
    // Check if it's already a proper ApiError format
    if ('code' in obj && 'message' in obj && typeof obj.code === 'string' && typeof obj.message === 'string') {
      return {
        error: { code: obj.code, message: obj.message },
        fieldErrors,
      };
    }
    
    // Handle Django's "detail" format
    if ('detail' in obj && typeof obj.detail === 'string') {
      return {
        error: { code: 'API_ERROR', message: obj.detail },
        fieldErrors,
      };
    }
    
    // Parse field-level validation errors (Django REST Framework format)
    // { "field_name": ["error1", "error2"], "another_field": ["error3"] }
    const errorMessages: string[] = [];
    
    for (const [key, value] of Object.entries(obj)) {
      if (Array.isArray(value) && value.every(v => typeof v === 'string')) {
        if (key === 'non_field_errors') {
          // General errors not tied to a specific field
          errorMessages.push(...value);
        } else {
          // Field-specific errors
          fieldErrors[key] = value;
          // Also add to general messages for backwards compatibility
          errorMessages.push(...value.map(msg => `${msg}`));
        }
      } else if (typeof value === 'string') {
        // Single string error for a field
        if (key === 'detail' || key === 'message' || key === 'error') {
          errorMessages.push(value);
        } else {
          fieldErrors[key] = [value];
          errorMessages.push(value);
        }
      }
    }
    
    if (errorMessages.length > 0 || Object.keys(fieldErrors).length > 0) {
      return {
        error: {
          code: 'VALIDATION_ERROR',
          message: errorMessages[0] || 'Please correct the errors below',
        },
        fieldErrors,
      };
    }
  }
  
  // Fallback
  return {
    error: { code: 'UNKNOWN_ERROR', message: 'An unexpected error occurred' },
    fieldErrors,
  };
}

export class ApiClientError extends Error {
  public readonly fieldErrors: FieldErrors;
  
  constructor(
    public readonly status: number,
    public readonly error: ApiError,
    fieldErrors?: FieldErrors
  ) {
    super(error.message);
    this.name = 'ApiClientError';
    this.fieldErrors = fieldErrors || {};
  }
  
  /**
   * Check if there are field-specific errors
   */
  hasFieldErrors(): boolean {
    return Object.keys(this.fieldErrors).length > 0;
  }
  
  /**
   * Get error message for a specific field
   */
  getFieldError(field: string): string | null {
    const errors = this.fieldErrors[field];
    return errors && errors.length > 0 ? errors[0] : null;
  }
  
  /**
   * Get all field errors as a single message per field
   */
  getFieldErrorMessages(): Record<string, string> {
    const result: Record<string, string> = {};
    for (const [field, errors] of Object.entries(this.fieldErrors)) {
      if (errors.length > 0) {
        result[field] = errors.join('. ');
      }
    }
    return result;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isValidationError(): boolean {
    return this.status === 400;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }
}

// =============================================================================
// Base Request Functions
// =============================================================================

async function refreshAccessToken(): Promise<string> {
  if (!refreshToken) {
    throw new ApiClientError(401, {
      code: 'NO_REFRESH_TOKEN',
      message: 'No refresh token available',
    });
  }

  const response = await fetch(`${API_BASE_URL}/auth/refresh/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh: refreshToken }),
  });

  if (!response.ok) {
    clearTokens();
    throw new ApiClientError(401, {
      code: 'TOKEN_REFRESH_FAILED',
      message: 'Session expired. Please log in again.',
    });
  }

  const data = await response.json();
  accessToken = data.access;
  return data.access;
}

async function getValidAccessToken(): Promise<string | null> {
  if (accessToken) {
    return accessToken;
  }

  if (refreshToken) {
    // Prevent multiple simultaneous refresh attempts
    if (!refreshPromise) {
      refreshPromise = refreshAccessToken().finally(() => {
        refreshPromise = null;
      });
    }
    return refreshPromise;
  }

  return null;
}

interface RequestOptions {
  method: string;
  path: string;
  body?: unknown;
  params?: Record<string, string>;
  requiresAuth?: boolean;
  useMfaToken?: boolean;
}

async function request<T>(options: RequestOptions): Promise<T> {
  const { method, path, body, params, requiresAuth = true, useMfaToken = false } = options;

  let url = `${API_BASE_URL}${path}`;
  if (params) {
    const searchParams = new URLSearchParams(params);
    url += `?${searchParams.toString()}`;
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (useMfaToken && mfaToken) {
    headers['X-MFA-Token'] = mfaToken;
  } else if (requiresAuth) {
    const token = await getValidAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const response = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    // If unauthorized and we have a refresh token, try refreshing
    if (response.status === 401 && refreshToken && requiresAuth && !useMfaToken) {
      try {
        await refreshAccessToken();
        // Retry the request with new token
        return request<T>({ ...options, requiresAuth: true });
      } catch {
        // Refresh failed, throw original error
      }
    }

    // Parse the error response to extract field-level errors
    const { error, fieldErrors } = parseApiError(response.status, data);
    throw new ApiClientError(response.status, error, fieldErrors);
  }

  return data;
}

// =============================================================================
// Authentication API
// =============================================================================

export const authApi = {
  /**
   * Register a new user and organization
   * Creates user, tenant (org), and membership
   * Returns MFA token to flow into MFA setup
   */
  async register(data: RegistrationRequest): Promise<RegistrationResponse> {
    const response = await request<RegistrationResponse>({
      method: 'POST',
      path: '/auth/register/',
      body: data,
      requiresAuth: false,
    });
    
    if (response.mfa_token) {
      setMfaToken(response.mfa_token);
    }
    
    return response;
  },

  /**
   * Login with email and password (step 1)
   * Returns MFA token for step 2
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await request<LoginResponse>({
      method: 'POST',
      path: '/auth/login/',
      body: credentials,
      requiresAuth: false,
    });
    
    if (response.mfa_token) {
      setMfaToken(response.mfa_token);
    }
    
    return response;
  },

  /**
   * Verify MFA code (step 2)
   * Returns access and refresh tokens
   */
  async verifyMfa(data: MFAVerifyRequest): Promise<TokenResponse> {
    const response = await request<TokenResponse>({
      method: 'POST',
      path: '/auth/mfa/verify/',
      body: data,
      requiresAuth: false,
    });
    
    setTokens(response.access, response.refresh);
    mfaToken = null;
    
    return response;
  },

  /**
   * Use recovery code instead of MFA
   */
  async useRecoveryCode(data: MFARecoveryRequest): Promise<TokenResponse> {
    const response = await request<TokenResponse>({
      method: 'POST',
      path: '/auth/mfa/recovery/',
      body: data,
      requiresAuth: false,
    });
    
    setTokens(response.access, response.refresh);
    mfaToken = null;
    
    return response;
  },

  /**
   * Get MFA setup data (QR code) for new users
   */
  async getMfaSetup(): Promise<MFASetupData> {
    return request<MFASetupData>({
      method: 'GET',
      path: '/auth/mfa/setup/',
      requiresAuth: false,
      useMfaToken: true,
    });
  },

  /**
   * Verify MFA setup and enable MFA
   */
  async verifyMfaSetup(data: MFASetupVerifyRequest): Promise<TokenWithRecoveryResponse> {
    const response = await request<TokenWithRecoveryResponse>({
      method: 'POST',
      path: '/auth/mfa/setup/verify/',
      body: data,
      requiresAuth: false,
      useMfaToken: true,
    });
    
    setTokens(response.access, response.refresh);
    mfaToken = null;
    
    return response;
  },

  /**
   * Logout and blacklist refresh token
   */
  async logout(): Promise<void> {
    if (refreshToken) {
      try {
        await request({
          method: 'POST',
          path: '/auth/logout/',
          body: { refresh: refreshToken },
        });
      } catch {
        // Ignore logout errors
      }
    }
    clearTokens();
  },

  /**
   * Get current user profile
   */
  async getMe(): Promise<User> {
    return request<User>({
      method: 'GET',
      path: '/auth/me/',
    });
  },

  /**
   * Refresh access token
   */
  async refresh(): Promise<{ access: string }> {
    const token = await refreshAccessToken();
    return { access: token };
  },
};

// =============================================================================
// Forms API
// =============================================================================

export const formsApi = {
  /**
   * List all forms
   */
  async list(params?: { status?: string; search?: string }): Promise<ApiResponse<FormListItem[]>> {
    return request<ApiResponse<FormListItem[]>>({
      method: 'GET',
      path: '/forms/',
      params: params as Record<string, string>,
    });
  },

  /**
   * Get a single form
   */
  async get(formId: string): Promise<Form> {
    return request<Form>({
      method: 'GET',
      path: `/forms/${formId}/`,
    });
  },

  /**
   * Create a new form
   */
  async create(data: FormCreateRequest): Promise<Form> {
    return request<Form>({
      method: 'POST',
      path: '/forms/',
      body: data,
    });
  },

  /**
   * Update a form
   */
  async update(formId: string, data: FormUpdateRequest): Promise<Form> {
    return request<Form>({
      method: 'PATCH',
      path: `/forms/${formId}/`,
      body: data,
    });
  },

  /**
   * Delete (archive) a form
   */
  async delete(formId: string): Promise<void> {
    return request<void>({
      method: 'DELETE',
      path: `/forms/${formId}/`,
    });
  },

  /**
   * Publish a form version
   */
  async publish(formId: string, data?: PublishRequest): Promise<PublishResponse> {
    return request<PublishResponse>({
      method: 'POST',
      path: `/forms/${formId}/publish/`,
      body: data || {},
    });
  },

  /**
   * Get form versions
   */
  async getVersions(formId: string): Promise<ApiResponse<Form['versions']>> {
    return request<ApiResponse<Form['versions']>>({
      method: 'GET',
      path: `/forms/${formId}/versions/`,
    });
  },

  /**
   * Get embed code
   */
  async getEmbedCode(formId: string): Promise<EmbedCodeResponse> {
    return request<EmbedCodeResponse>({
      method: 'GET',
      path: `/forms/${formId}/embed-code/`,
    });
  },

  /**
   * Duplicate a form
   */
  async duplicate(formId: string, name?: string): Promise<Form> {
    return request<Form>({
      method: 'POST',
      path: `/forms/${formId}/duplicate/`,
      body: name ? { name } : {},
    });
  },
};

// =============================================================================
// Steps API
// =============================================================================

export const stepsApi = {
  /**
   * List steps for a form
   */
  async list(formId: string): Promise<ApiResponse<FormStep[]>> {
    return request<ApiResponse<FormStep[]>>({
      method: 'GET',
      path: `/forms/${formId}/steps/`,
    });
  },

  /**
   * Create a step
   */
  async create(formId: string, data: StepCreateRequest): Promise<FormStep> {
    return request<FormStep>({
      method: 'POST',
      path: `/forms/${formId}/steps/`,
      body: data,
    });
  },

  /**
   * Get a step
   */
  async get(stepId: string): Promise<FormStep> {
    return request<FormStep>({
      method: 'GET',
      path: `/steps/${stepId}/`,
    });
  },

  /**
   * Update a step
   */
  async update(stepId: string, data: Partial<StepCreateRequest>): Promise<FormStep> {
    return request<FormStep>({
      method: 'PATCH',
      path: `/steps/${stepId}/`,
      body: data,
    });
  },

  /**
   * Delete a step
   */
  async delete(stepId: string): Promise<void> {
    return request<void>({
      method: 'DELETE',
      path: `/steps/${stepId}/`,
    });
  },

  /**
   * Reorder steps
   */
  async reorder(formId: string, data: ReorderRequest): Promise<ApiResponse<FormStep[]>> {
    return request<ApiResponse<FormStep[]>>({
      method: 'POST',
      path: `/forms/${formId}/steps/reorder/`,
      body: data,
    });
  },
};

// =============================================================================
// Variants API
// =============================================================================

export const variantsApi = {
  /**
   * List variants for a step
   */
  async list(stepId: string): Promise<ApiResponse<StepVariant[]>> {
    return request<ApiResponse<StepVariant[]>>({
      method: 'GET',
      path: `/steps/${stepId}/variants/`,
    });
  },

  /**
   * Create a variant
   */
  async create(stepId: string, data: VariantCreateRequest): Promise<StepVariant> {
    return request<StepVariant>({
      method: 'POST',
      path: `/steps/${stepId}/variants/`,
      body: data,
    });
  },

  /**
   * Get a variant
   */
  async get(variantId: string): Promise<StepVariant> {
    return request<StepVariant>({
      method: 'GET',
      path: `/variants/${variantId}/`,
    });
  },

  /**
   * Update a variant
   */
  async update(variantId: string, data: Partial<VariantCreateRequest>): Promise<StepVariant> {
    return request<StepVariant>({
      method: 'PATCH',
      path: `/variants/${variantId}/`,
      body: data,
    });
  },

  /**
   * Delete a variant
   */
  async delete(variantId: string): Promise<void> {
    return request<void>({
      method: 'DELETE',
      path: `/variants/${variantId}/`,
    });
  },

  /**
   * Duplicate a variant
   */
  async duplicate(variantId: string): Promise<StepVariant> {
    return request<StepVariant>({
      method: 'POST',
      path: `/variants/${variantId}/duplicate/`,
    });
  },
};

// =============================================================================
// Fields API
// =============================================================================

export const fieldsApi = {
  /**
   * List fields for a variant
   */
  async list(variantId: string): Promise<ApiResponse<FormField[]>> {
    return request<ApiResponse<FormField[]>>({
      method: 'GET',
      path: `/variants/${variantId}/fields/`,
    });
  },

  /**
   * Create a field
   */
  async create(variantId: string, data: FieldCreateRequest): Promise<FormField> {
    return request<FormField>({
      method: 'POST',
      path: `/variants/${variantId}/fields/`,
      body: data,
    });
  },

  /**
   * Get a field
   */
  async get(fieldId: string): Promise<FormField> {
    return request<FormField>({
      method: 'GET',
      path: `/fields/${fieldId}/`,
    });
  },

  /**
   * Update a field
   */
  async update(fieldId: string, data: Partial<FieldCreateRequest>): Promise<FormField> {
    return request<FormField>({
      method: 'PATCH',
      path: `/fields/${fieldId}/`,
      body: data,
    });
  },

  /**
   * Delete a field
   */
  async delete(fieldId: string): Promise<void> {
    return request<void>({
      method: 'DELETE',
      path: `/fields/${fieldId}/`,
    });
  },

  /**
   * Reorder fields
   */
  async reorder(variantId: string, data: ReorderRequest): Promise<ApiResponse<FormField[]>> {
    return request<ApiResponse<FormField[]>>({
      method: 'POST',
      path: `/variants/${variantId}/fields/reorder/`,
      body: data,
    });
  },
};

// =============================================================================
// Submissions API
// =============================================================================

export const submissionsApi = {
  /**
   * List submissions for a form (with pagination)
   */
  async listForForm(formId: string, params?: {
    status?: string;
    date_from?: string;
    date_to?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedSubmissions> {
    const queryParams: Record<string, string> = {};
    if (params?.status) queryParams.status = params.status;
    if (params?.date_from) queryParams.date_from = params.date_from;
    if (params?.date_to) queryParams.date_to = params.date_to;
    if (params?.page) queryParams.page = String(params.page);
    if (params?.page_size) queryParams.page_size = String(params.page_size);

    return request<PaginatedSubmissions>({
      method: 'GET',
      path: `/forms/${formId}/submissions/`,
      params: queryParams,
    });
  },

  /**
   * Get submission detail (metadata only, no PHI)
   * ⚠️ HIPAA: Actual PHI requires decrypt endpoint with MFA
   */
  async get(submissionId: string): Promise<SubmissionDetail> {
    return request<SubmissionDetail>({
      method: 'GET',
      path: `/submissions/${submissionId}/`,
    });
  },

  /**
   * Decrypt PHI data for a submission
   * ⚠️ PHI WARNING: Requires MFA verification
   * Every call is logged to audit trail
   * Keep data in memory only - never persist
   */
  async decrypt(submissionId: string, data: DecryptRequest): Promise<DecryptedSubmissionData> {
    return request<DecryptedSubmissionData>({
      method: 'POST',
      path: `/submissions/${submissionId}/decrypt/`,
      body: data,
    });
  },

  /**
   * Get submission statistics for a form
   */
  async getStats(formId: string): Promise<SubmissionStats> {
    return request<SubmissionStats>({
      method: 'GET',
      path: `/forms/${formId}/submissions/stats/`,
    });
  },

  /**
   * Export submissions for a form
   * ⚠️ PHI WARNING: Exporting with PHI requires MFA
   * Returns a download URL or triggers download
   */
  async export(formId: string, data: SubmissionExportRequest): Promise<Blob> {
    const token = await getValidAccessToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/forms/${formId}/submissions/export/`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new ApiClientError(response.status, errorData || {
        code: 'EXPORT_FAILED',
        message: 'Failed to export submissions',
      });
    }

    return response.blob();
  },

  /**
   * Perform bulk action on submissions
   */
  async bulkAction(data: BulkActionRequest): Promise<BulkActionResponse> {
    return request<BulkActionResponse>({
      method: 'POST',
      path: '/submissions/bulk-action/',
      body: data,
    });
  },

  /**
   * List all submissions (across forms) - for admin view
   */
  async listAll(params?: {
    form?: string;
    status?: string;
    date_from?: string;
    date_to?: string;
    search?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedSubmissions> {
    const queryParams: Record<string, string> = {};
    if (params?.form) queryParams.form = params.form;
    if (params?.status) queryParams.status = params.status;
    if (params?.date_from) queryParams.date_from = params.date_from;
    if (params?.date_to) queryParams.date_to = params.date_to;
    if (params?.search) queryParams.search = params.search;
    if (params?.page) queryParams.page = String(params.page);
    if (params?.page_size) queryParams.page_size = String(params.page_size);

    return request<PaginatedSubmissions>({
      method: 'GET',
      path: '/submissions/',
      params: queryParams,
    });
  },
};

// =============================================================================
// Analytics API
// =============================================================================

/** Global analytics overview response */
export interface GlobalAnalyticsOverview {
  period: {
    start_date: string;
    end_date: string;
  };
  metrics: {
    total_forms: number;
    active_forms: number;
    total_submissions: number;
    submissions_today: number;
    conversion_rate: number;
  };
  submission_trend: Array<{
    date: string;
    submissions: number;
  }>;
  top_forms: Array<{
    form_id: string;
    form_name: string;
    submission_count: number;
  }>;
}

/** Recent activity response */
export interface RecentActivity {
  recent_submissions: Array<{
    id: string;
    form_id: string;
    form_name: string;
    submitted_at: string;
    status: string;
  }>;
  recent_forms: Array<{
    id: string;
    name: string;
    status: string;
    updated_at: string;
  }>;
}

export const analyticsApi = {
  /**
   * Get overview metrics for a form
   */
  async getOverview(formId: string, params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<AnalyticsOverview> {
    return request<AnalyticsOverview>({
      method: 'GET',
      path: `/analytics/forms/${formId}/overview/`,
      params: params as Record<string, string>,
    });
  },

  /**
   * Get funnel data for a form
   */
  async getFunnel(formId: string, params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<FunnelData> {
    return request<FunnelData>({
      method: 'GET',
      path: `/analytics/forms/${formId}/funnel/`,
      params: params as Record<string, string>,
    });
  },

  /**
   * Get A/B test results for a form
   */
  async getVariants(formId: string, params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<ABTestResults> {
    return request<ABTestResults>({
      method: 'GET',
      path: `/analytics/forms/${formId}/variants/`,
      params: params as Record<string, string>,
    });
  },

  /**
   * Get global analytics overview for dashboard
   */
  async getGlobalOverview(params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<GlobalAnalyticsOverview> {
    return request<GlobalAnalyticsOverview>({
      method: 'GET',
      path: '/analytics/overview/',
      params: params as Record<string, string>,
    });
  },

  /**
   * Get recent activity for dashboard
   */
  async getRecentActivity(limit?: number): Promise<RecentActivity> {
    const params: Record<string, string> = {};
    if (limit) params.limit = String(limit);
    
    return request<RecentActivity>({
      method: 'GET',
      path: '/analytics/recent-activity/',
      params,
    });
  },
};

// =============================================================================
// Team API
// =============================================================================

/** Role info returned from the API */
export interface RoleInfo {
  name: string;
  display_name: string;
  description: string;
  hierarchy_level: number;
}

/** Team statistics */
export interface TeamStats {
  total_members: number;
  by_role: {
    owner: number;
    admin: number;
    editor: number;
    viewer: number;
  };
  pending_invitations: number;
}

export const teamApi = {
  /**
   * List team members
   */
  async list(): Promise<ApiResponse<TeamMember[]>> {
    return request<ApiResponse<TeamMember[]>>({
      method: 'GET',
      path: '/team/members/',
    });
  },

  /**
   * Get a single team member
   */
  async get(memberId: string): Promise<ApiResponse<TeamMember>> {
    return request<ApiResponse<TeamMember>>({
      method: 'GET',
      path: `/team/members/${memberId}/`,
    });
  },

  /**
   * Invite a new team member
   */
  async invite(data: InviteUserRequest): Promise<ApiResponse<TeamMember>> {
    return request<ApiResponse<TeamMember>>({
      method: 'POST',
      path: '/team/members/',
      body: data,
    });
  },

  /**
   * Update a team member's role
   */
  async updateRole(memberId: string, data: UpdateUserRoleRequest): Promise<ApiResponse<TeamMember>> {
    return request<ApiResponse<TeamMember>>({
      method: 'PATCH',
      path: `/team/members/${memberId}/`,
      body: data,
    });
  },

  /**
   * Remove a team member
   */
  async remove(memberId: string): Promise<void> {
    return request<void>({
      method: 'DELETE',
      path: `/team/members/${memberId}/`,
    });
  },

  /**
   * Resend invitation to a team member
   */
  async resendInvitation(memberId: string): Promise<{ message: string }> {
    return request<{ message: string }>({
      method: 'POST',
      path: `/team/members/${memberId}/resend/`,
    });
  },

  /**
   * Get available roles
   */
  async getRoles(): Promise<ApiResponse<RoleInfo[]>> {
    return request<ApiResponse<RoleInfo[]>>({
      method: 'GET',
      path: '/team/roles/',
    });
  },

  /**
   * Get team statistics
   */
  async getStats(): Promise<ApiResponse<TeamStats>> {
    return request<ApiResponse<TeamStats>>({
      method: 'GET',
      path: '/team/stats/',
    });
  },
};

// =============================================================================
// Tenant API
// =============================================================================

export const tenantApi = {
  /**
   * List all tenants the current user belongs to
   */
  async listUserTenants(): Promise<TenantListItem[]> {
    const response = await request<{ data: TenantListItem[] }>({
      method: 'GET',
      path: '/tenants/me',
    });
    return response.data;
  },

  /**
   * Get current tenant info (from JWT context)
   */
  async getCurrentTenant(): Promise<Tenant> {
    const response = await request<{ data: Tenant }>({
      method: 'GET',
      path: '/tenants/current',
    });
    return response.data;
  },

  /**
   * Switch to a different tenant
   * Returns new JWT tokens scoped to the new tenant
   */
  async switchTenant(tenantSlug: string): Promise<TenantSwitchResponse> {
    const response = await request<{ data: TenantSwitchResponse }>({
      method: 'POST',
      path: '/tenants/switch',
      body: { tenant_slug: tenantSlug },
    });

    // Update stored tokens with new tenant-scoped tokens
    setTokens(response.data.access, response.data.refresh);

    return response.data;
  },
};

// =============================================================================
// Combined API Export
// =============================================================================

export const api = {
  auth: authApi,
  forms: formsApi,
  steps: stepsApi,
  variants: variantsApi,
  fields: fieldsApi,
  submissions: submissionsApi,
  analytics: analyticsApi,
  team: teamApi,
  tenants: tenantApi,
};

export default api;
